import React from 'react'
import {useModal} from '_context'
import {NewsletterModal, SwitchLocaleModal, LocaleModal, WishlistSizeModal} from '_uiComponents'
import {useIntl} from 'react-intl'
import {usePageDesigner} from '_customHooks'

/**
 * Interface for newsletter modal data
 * @interface NewsletterData
 * @property {string} [newsletterValue] - Optional pre-filled value for the newsletter input
 * @property {string} analyticsLabel - Label used for analytics tracking
 * @property {Function} [analyticsEventNewsletter] - Optional callback function for newsletter analytics events
 * @property {string} [overlayId] - Optional class_name of the PD component to render in the modal
 */
interface NewsletterData {
    newsletterValue?: string
    analyticsLabel: string
    analyticsEventNewsletter?: (event: {action: string; label: string}) => void
    overlayId?: string
}

/**
 * Interface for switch locale modal data
 * @interface SwitchLocaleData
 * @property {Object} labels - Text and image content for the modal
 * @property {string} labels.title - Modal title
 * @property {string} labels.description - Modal description
 * @property {Object} labels.imageUrl - Responsive image URLs
 * @property {Object} locale - Locale configuration data
 * @property {Array<{label: string, value: string}>} locale.countries - List of available countries
 * @property {{label: string, value: string}} locale.suggested - Suggested locale
 * @property {{label: string, value: string}} locale.current - Current locale
 * @property {Function} [onClose] - Optional callback function when modal closes
 */
interface SwitchLocaleData {
    labels: {
        title: string
        description: string
        imageUrl: {
            desktop: string
            tablet: string
            mobile: string
        }
    }
    locale: {
        countries: {label: string; value: string}[]
        suggested: {label: string; value: string}
        current: {label: string; value: string}
    }
    onClose?: () => void
}
/**
 * Interface for locale modal data
 * @interface LocaleData
 * @property {Function} [onClose] - Optional callback function when modal closes
 */
interface LocaleData {
    onClose?: () => void
}

/**
 * Interface for wishlist size modal data
 * @interface WishlistSizeData
 * @property {boolean} shouldOpenMiniCartOnClose - Flag to determine if mini cart should open after modal closes
 */
interface WishlistSizeData {
    shouldOpenMiniCartOnClose: boolean
}

/**
 * Custom hook for managing different types of modals
 * @returns {Object} Object containing functions to open different types of modals
 * @property {(data: NewsletterData) => void} openNewsletterModal - Opens the newsletter subscription modal
 * @property {(data: SwitchLocaleData) => void} openSwitchLocaleModal - Opens the locale switching modal
 * @property {(data: LocaleData) => void} openLocaleModal - Opens the locale selection modal
 * @property {(data: WishlistSizeData) => void} openWishlistSize - Opens the wishlist size selection modal
 */
export const useOpenModal = () => {
    const {openModal} = useModal()
    const {formatMessage} = useIntl()
    // PD data for the 'overlay-newsletter' page, needed by the dynamic trigger to route by overlay_style.
    // Called at the top level of the hook (React hook rules); referenced inside openNewsletterOverlay.
    const {pageComponents} = usePageDesigner({pageId: 'overlay-newsletter'})

    /**
     * Opens the newsletter subscription modal
     * @param {NewsletterData} data - Data required for the newsletter modal
     */
    const openNewsletterModal = (data: NewsletterData) => {
        openModal({
            title: formatMessage({id: 'label.newsletter', defaultMessage: 'Newsletter'}),
            imageUrl: '',
            variant: 'NewsletterModal',
            body: (
                <NewsletterModal overlayId={data.overlayId} emailInputFooter={data.newsletterValue} analyticsLabel={data.analyticsLabel} />
            ),
            onClose: () => {
                data.analyticsEventNewsletter?.({action: 'dismiss', label: data.analyticsLabel})
            },
            className: `cc-pd-overlay-newsletter ${data.overlayId ?? ''}`.trim(),
        })
    }

    /**
     * Dynamic newsletter trigger: resolves the layout by looking up the PD component whose
     * `class_name` matches the given overlay id, then routes to the correct mechanism.
     *
     * - `overlay_style === 'small'` → dispatch the `overlaynewsletter:showmodal` DOM event (the
     *   legacy/personalization mechanism served by NewsletterModalSmall), unchanged.
     * - otherwise (`image` / non-small, or component not found) → open the React `NewsletterModal`
     *   overlay parameterised to render that specific component by id.
     *
     * The front-end only passes the id; layout knowledge lives entirely here.
     *
     * @param {string} id - The overlay id (PD component class_name) carried by the CTA.
     * @param {string} source - Analytics/source label (e.g. 'header_stripbar').
     * @param {string} [email] - Optional pre-filled email to forward to the modal.
     */
    const openNewsletterOverlay = (id: string, source: string, email?: string) => {
        const components = pageComponents?.regions?.[0]?.components
        const comp = components?.find((c) => c?.data?.class_name === id)

        if (comp?.data?.overlay_style === 'small') {
            document.dispatchEvent(
                new CustomEvent('overlaynewsletter:showmodal', {
                    detail: {target: id, source, ...(email ? {email} : {})},
                }),
            )
            return
        }

        // Non-small overlays (and the not-found fallback for found components) open the React overlay.
        if (comp) {
            openNewsletterModal({overlayId: id, analyticsLabel: source, newsletterValue: email})
            return
        }

        // Component not found in the PD page: fall back to the small-event dispatch so behaviour is
        // never worse than before (see implementation report caveat).
        document.dispatchEvent(
            new CustomEvent('overlaynewsletter:showmodal', {
                detail: {target: id, source, ...(email ? {email} : {})},
            }),
        )
    }

    /**
     * Opens the locale switching modal
     * @param {SwitchLocaleData} data - Data required for the switch locale modal
     */
    const openSwitchLocaleModal = (data: SwitchLocaleData) => {
        openModal({
            body: <SwitchLocaleModal labels={data.labels} locale={data.locale} />,
            imageUrl: '',
            title: data.labels.title,
            variant: 'switchLocale',
            onClose: data.onClose,
        })
    }

    /**
     * Opens the locale selection modal
     * @param {LocaleData} data - Data required for the locale modal
     */
    const openLocaleModal = (data: LocaleData) => {
        openModal({
            body: (
                <LocaleModal
                    title={formatMessage({id: 'label.form.title', defaultMessage: 'Cambia Paese/Lingua'})}
                    description={formatMessage({
                        id: 'label.form.desc',
                        defaultMessage:
                            'Ti ricordiamo che il catalogo, i servizi e i prezzi possono cambiare in base al Paese selezionato. Eventuali prodotti nel carrello verranno rimossi.',
                    })}
                />
            ),
            imageUrl: '',
            title: formatMessage({id: 'label.form.title', defaultMessage: 'Cambia Paese/Lingua'}),
            variant: 'changeLocale',
            onClose: data.onClose,
        })
    }

    /**
     * Opens the wishlist size selection modal
     * @param {WishlistSizeData} data - Data required for the wishlist size modal
     */
    const openWishlistSize = (data: WishlistSizeData) => {
        openModal({
            body: <WishlistSizeModal shouldOpenMiniCartOnClose={data.shouldOpenMiniCartOnClose} />,
            variant: 'wishlistSize',
            closingIconSize: 'lg',
        })
    }

    return {openNewsletterModal, openNewsletterOverlay, openSwitchLocaleModal, openLocaleModal, openWishlistSize}
}
